import { AggregateSelect, resolveAggregateLabel } from './AggregateSelect';
import { text1, header4 } from '@ardoq/typography';
import { CSSProperties, default as React, useMemo, useState } from 'react';
import styled from 'styled-components';
import { colors, s16, s8 } from '@ardoq/design-tokens';
import { isTruthy, isJust } from '@ardoq/common-helpers';
import {
  DatasourceTable,
  newTableTheme,
  TableComponents,
} from '@ardoq/table-legacy';
import {
  AggregatesResultsState,
  DataSourceRow,
  GetComponentLinkHandler,
  GetComponentNavigationOptions,
  HasComponentReadPermission,
  ReportPageAndPagination,
  ReportSort,
  SearchResults,
} from './types';
import { enhancePartialScopeData, responsiveRowHeight } from '@ardoq/renderers';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { EnhancedScopeData } from '@ardoq/data-model';
import {
  Aggregate,
  APIFieldType,
  APISearchAggregatesResponse,
  ArdoqId,
  columnPredicates,
  ReportColumn,
  ReportColumnType,
  UserDataForRenderers,
} from '@ardoq/api-types';
import { PaginationBar } from '@ardoq/pagination';
import { getHeaderRender } from './headerRender';
import { getValueRender } from './valueRender';
import { EmptyResultsDoqy } from './atoms';
import { useReportTracking } from './tracking/ReportTrackingContext';
import { BUTTON_SELECTOR } from '@ardoq/button';
import { formatNumber, Locale } from '@ardoq/locale';
import { noop } from 'lodash';
import { StatusType, Tag } from '@ardoq/status-ui';
import { IconName } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { getColumnOriginalLabel } from './utils';
import { CaptureResultsSummary } from './CaptureResultsSummary';
import type { SearchResultsTableLabels } from './labels';

const FullSizeAggregateRowComponent = styled(newTableTheme.BodyTr)`
  td {
    ${header4};
    position: sticky;
    bottom: 0;
    background: ${colors.white950};
  }

  td:first-child {
    ${header4};
    padding-left: 0;
    position: sticky;
    bottom: 0;
    background: ${colors.white950};
  }
`;

const WidgetAggregateRowComponent = styled(FullSizeAggregateRowComponent)`
  td {
    position: unset;
  }

  td:first-child {
    position: unset;
  }
`;

const BodyTr = styled(newTableTheme.BodyTr)`
  td {
    ${text1};
    ${responsiveRowHeight};
    min-height: 20px;
  }

  td:first-child {
    ${text1};
    ${responsiveRowHeight};
    padding-left: 0;
    min-height: 20px;
  }

  /* for renderers that have 2 nested divs */

  td:first-child div {
    ${responsiveRowHeight};
  }
`;

const HeadTr = styled(newTableTheme.HeadTr)`
  th:first-child div {
    padding-left: 0;
  }

  ${BUTTON_SELECTOR} {
    margin-left: ${s8};
  }

  th {
    white-space: nowrap;
    padding: 0;
  }
`;

const Tfoot = styled(newTableTheme.Tfoot)`
  position: sticky;
  inset-block-end: 0;
  background: ${colors.white950};
`;
const numberColumnCellStyle = {
  textAlign: 'right',
  '--justify': 'flex-end',
} as const;

const isNumericListField = (
  column: ReportColumn,
  scopeData: SearchResults['scopeData']
): boolean => {
  // We have fields like "Business Strategic Fit" which is a list field but contains numeric values. For such fields we also want to apply number formatting and right alignment.
  // The only way to identify such fields is to check if it's a list field and if its default value is a comma separated list of numbers.

  const field = scopeData?.fields.find(f => f.name === column.key);
  if (
    field?.type === APIFieldType.LIST &&
    typeof field.defaultValue === 'string' &&
    field.defaultValue !== ''
  )
    return field.defaultValue
      .split(',')
      .every(listOption => !isNaN(Number(listOption.trim())));

  return false;
};

const getColumnDescription = (
  column: ReportColumn,
  enhancedScopeData: EnhancedScopeData
): string | null => {
  if (
    column.type === ReportColumnType.FIELD ||
    column.type === ReportColumnType.CUSTOM
  ) {
    return (
      enhancedScopeDataOperations.getFieldByName(enhancedScopeData, column.key)
        ?.description ?? null
    );
  }

  if (
    column.type === ReportColumnType.REFERENCE_TYPE_INCOMING ||
    column.type === ReportColumnType.REFERENCE_TYPE_OUTGOING
  ) {
    for (const model of enhancedScopeData.models) {
      const refType = Object.values(model.referenceTypes).find(
        rt => rt.name === getColumnOriginalLabel(column)
      );
      if (refType) {
        return refType.description ?? null;
      }
    }
  }
  return null;
};

const DEFAULT_AGGREGATES_ERROR_TEMPLATE = (traceId: string): string =>
  `Error loading aggregates. Trace ID: ${traceId}`;

const CAPTURE_MODE_TABLE_STYLE: CSSProperties = { overflowY: 'visible' };

interface SearchResultsTableProps {
  users: Record<ArdoqId, UserDataForRenderers>;
  dataSourceLabel?: string;
  searchAggregatesResults?: APISearchAggregatesResponse | null;
  searchResults: SearchResults;
  aggregatesResultsState: AggregatesResultsState;
  isAggregatesBeingExecuted?: boolean;
  builderSelectedAggregate: null | Aggregate;
  getComponentLinkHandler?: GetComponentLinkHandler;
  getComponentNavigationOptions?: GetComponentNavigationOptions;
  hasComponentReadPermission?: HasComponentReadPermission;
  currentUserLocale: Locale;
  isSearchBeingExecuted?: boolean;
  isInteractive: boolean;
  isEntityNavigationDisabled?: boolean;
  isCaptureMode?: boolean;
  isWidget?: boolean;
  variant?: 'default' | 'light';
  style?: CSSProperties;
  showAggregate: boolean;
  components?: Partial<TableComponents>;
  withPopover?: boolean;
  pageAndPaginationState?: [
    ReportPageAndPagination,
    (prev: ReportPageAndPagination) => void,
  ];
  sort?: ReportSort;
  setSort?: (sort: ReportSort) => void;
  indicateRenamedColumn?: boolean;
  selectedConfidentialFieldNames?: string[];
  labels?: SearchResultsTableLabels;
  ariaLabel?: string;
}

const SearchResultsTable = ({
  users,
  dataSourceLabel,
  searchAggregatesResults = null,
  searchResults: { results, columns, scopeData },
  aggregatesResultsState,
  builderSelectedAggregate,
  getComponentLinkHandler,
  getComponentNavigationOptions,
  hasComponentReadPermission,
  currentUserLocale,
  isSearchBeingExecuted,
  isAggregatesBeingExecuted,
  isInteractive,
  isEntityNavigationDisabled,
  isCaptureMode,
  isWidget = false,
  variant = 'default',
  style,
  showAggregate,
  components = {},
  withPopover,
  pageAndPaginationState,
  sort,
  setSort,
  indicateRenamedColumn, // only in report builder preview section, this is true
  selectedConfidentialFieldNames,
  labels,
  ariaLabel,
}: SearchResultsTableProps) => {
  const aggregatesErrorTemplate =
    labels?.aggregatesErrorTemplate ?? DEFAULT_AGGREGATES_ERROR_TEMPLATE;
  const [pageAndPagination, setPageAndPagination] =
    isInteractive && pageAndPaginationState
      ? pageAndPaginationState
      : [null, noop];
  const [viewerSelectedAggregate, setViewerSelectedAggregate] = useState(
    builderSelectedAggregate
  );
  const { trackSorting } = useReportTracking();

  const enhancedScopeData = scopeData
    ? enhancePartialScopeData(scopeData)
    : null;

  const numberColumnKeys = useMemo(
    () =>
      new Set(
        columns
          .filter(
            column =>
              columnPredicates.isNumberColumn(column) ||
              scopeData?.fields.some(
                f => f.name === column.key && f.type === APIFieldType.NUMBER
              ) ||
              isNumericListField(column, scopeData)
          )
          .map(({ key }) => key)
      ),
    [columns, scopeData]
  );

  const columnsToNotTruncate = ['name', 'source', 'target', 'parent'];
  const tableColumns = columns.map(column => ({
    dataIndex: column.key,
    sortOrder: column.key === sort?.columnKey ? sort.order : null,
    cellStyle: {
      whiteSpace: 'nowrap',
      ...(columnsToNotTruncate.includes(column.key) ||
      numberColumnKeys.has(column.key)
        ? {}
        : { maxWidth: 480 }), // component name and number columns should not be truncated
      ...(variant === 'light' && numberColumnKeys.has(column.key)
        ? numberColumnCellStyle
        : {}),
    } as CSSProperties,
    headerRender: getHeaderRender({
      sort,
      column,
      setSort,
      isInteractive,
      trackSorting,
      withPopover,
      indicateRenamedColumn,
      showConfidentialWarningIcon:
        !!selectedConfidentialFieldNames &&
        selectedConfidentialFieldNames.includes(column.key),
      columnDescription: enhancedScopeData
        ? getColumnDescription(column, enhancedScopeData)
        : null,
      isRightAligned: variant === 'light' && numberColumnKeys.has(column.key),
    }),
    valueRender: getValueRender({
      enhancedScopeData,
      users,
      getComponentLinkHandler,
      getComponentNavigationOptions,
      isInteractive,
      isEntityNavigationDisabled,
      column,
      hasComponentReadPermission,
      underlineOnHover: true,
      variant,
    }),
  }));

  const { aggregateResults, aggregateError } = aggregatesResultsState;

  const aggregateResult = aggregateResults?.aggregates;
  const totalNumberOfRows = aggregateResults?.totalNumberOfRows;
  const pagedResults =
    isInteractive && pageAndPagination && totalNumberOfRows
      ? { pageAndPagination, totalNumberOfRows }
      : null;

  const showAggregatesInFooter =
    Boolean(
      isJust(aggregateResult) &&
      (totalNumberOfRows || isWidget) &&
      builderSelectedAggregate &&
      showAggregate
    ) && !aggregateError;

  const TableComponents = {
    ...newTableTheme,
    BodyTr,
    ...components,
    HeadTr,
    Tfoot,
  };

  const aggregates =
    (searchAggregatesResults?.aggregates ?? aggregateResult) || null;

  const selectedAggregate =
    isInteractive && !isCaptureMode
      ? viewerSelectedAggregate
      : builderSelectedAggregate;

  const dataSource = results.filter(isTruthy);

  if (dataSource.length === 0) {
    return <EmptyResultsDoqy labels={labels?.emptyResults} />;
  }

  const hasFootContent = showAggregatesInFooter || aggregateError;

  return (
    <>
      <DatasourceTable
        fixedHeader
        maxWidth="auto"
        components={TableComponents}
        style={{
          paddingBottom: hasFootContent ? undefined : s16,
          ...(isCaptureMode ? CAPTURE_MODE_TABLE_STYLE : {}),
          ...style,
        }}
        ariaLabel={ariaLabel}
        // TODO: Report:
        // After applying more detailed TS check on Datasource
        // Potential issues here are detected
        // Datasource has different row types than expected in
        // value renderer. It's a valid TS feedback.
        // For now I have applied this typecasting here
        // as DataSourceRow[]
        dataSource={dataSource as DataSourceRow[]}
        tfootContent={
          showAggregatesInFooter ? (
            <>
              <TableComponents.HeadTr>
                {columns.map((_, index) => (
                  <TableComponents.Th key={index}>
                    {index ? null : (!isInteractive || isCaptureMode) &&
                      selectedAggregate ? (
                      resolveAggregateLabel(
                        selectedAggregate,
                        labels?.aggregateLabels
                      )
                    ) : (
                      <AggregateSelect
                        aggregates={aggregates}
                        onAggregateSelect={setViewerSelectedAggregate}
                        selectedAggregate={selectedAggregate}
                        aggregateLabels={labels?.aggregateLabels}
                      />
                    )}
                  </TableComponents.Th>
                ))}
              </TableComponents.HeadTr>
              <TableComponents.BodyTr>
                {columns.map((column, index) => {
                  const aggregateValue =
                    aggregates && selectedAggregate
                      ? aggregates[column.key]?.[selectedAggregate]
                      : null;

                  return (
                    <TableComponents.Td
                      key={index}
                      style={
                        variant === 'light' && numberColumnKeys.has(column.key)
                          ? numberColumnCellStyle
                          : undefined
                      }
                    >
                      {aggregateValue === null
                        ? null
                        : formatNumber(
                            aggregateValue,
                            selectedAggregate === Aggregate.COUNT
                              ? 'default'
                              : enhancedScopeData?.fields.find(
                                  field => field.name === column.key
                                )?.numberFormatOptions,
                            currentUserLocale
                          )}
                    </TableComponents.Td>
                  );
                })}
              </TableComponents.BodyTr>
            </>
          ) : (
            aggregateError && (
              <>
                <TableComponents.HeadTr>
                  <FlexBox
                    backgroundColor={'bgDefault'}
                    width={'full'}
                    paddingTop={'medium'}
                  >
                    <Tag
                      leftIcon={{ name: IconName.ERROR }}
                      statusType={StatusType.ERROR}
                      label={aggregatesErrorTemplate(
                        aggregateError.traceId ?? ''
                      )}
                    />
                  </FlexBox>
                </TableComponents.HeadTr>
              </>
            )
          )
        }
        getRowComponent={({ footerTitleRow, footerDataRow }) =>
          footerTitleRow || footerDataRow
            ? isWidget
              ? WidgetAggregateRowComponent
              : FullSizeAggregateRowComponent
            : TableComponents.BodyTr
        }
        columns={tableColumns}
        loading={isSearchBeingExecuted || isAggregatesBeingExecuted}
      />
      {pagedResults && isCaptureMode && (
        <CaptureResultsSummary
          currentPageNumber={pagedResults.pageAndPagination.page}
          perPage={pagedResults.pageAndPagination.pagination.size}
          totalResults={pagedResults.totalNumberOfRows}
          resultTypeLabel={dataSourceLabel}
        />
      )}
      {pagedResults && !isCaptureMode && (
        <PaginationBar
          pageSizeOptions={[10, 25, 100, 500]}
          isDisabled={isSearchBeingExecuted}
          style={{
            justifyContent: 'center',
            marginTop: s16,
          }}
          perPage={pagedResults.pageAndPagination.pagination.size}
          resultTypeLabel={dataSourceLabel}
          totalResults={pagedResults.totalNumberOfRows}
          currentPageNumber={pagedResults.pageAndPagination.page}
          onChange={({ currentPageNumber, perPage }) => {
            setPageAndPagination({
              page: currentPageNumber,
              pagination: {
                from: (currentPageNumber - 1) * perPage,
                size: perPage,
              },
            });
          }}
        />
      )}
    </>
  );
};

export { SearchResultsTable };
